import { forwardRef } from 'react';
import type { PropsWithoutRef, ReactNode, Ref } from 'react';
import styled, { css } from 'styled-components';

import { StyledAllTabsWrapper, StyledTabs } from '../Tabs/Tabs.styles';
import { defaultThemeProp } from '../../theme';
import Flex from '../Flex';
import type { BaseProps, ForwardRefForwardPropsComponent } from '../../types';
import { StyledPopover } from '../Popover';
import { StyledProgressBackdrop } from '../Progress';

import { StyledCard } from './Card';
import { StyledCardContent } from './CardContent';

export interface CardHeaderProps extends BaseProps {
  /** The Card header content. */
  children: ReactNode;
  /** Action Buttons that will render within the header. */
  actions?: ReactNode;
  /** Ref for the wrapping element. */
  ref?: Ref<HTMLDivElement>;
}

export const StyledCardHeader = styled.header<CardHeaderProps>(({ theme, onClick }) => {
  return css`
    ${StyledPopover} &, &:not(${StyledCard} ${StyledCard} > &) {
      padding: calc(1.5 * ${theme.base.spacing}) calc(${theme.components.card.padding} * 2);

      @media (width < ${theme.base.breakpoints.sm}) {
        padding: ${theme.base.spacing} calc(${theme.components.card.padding} * 1.5);
      }

      &:has(${StyledTabs}:first-child),
      &:has(${StyledAllTabsWrapper}:first-child) {
        padding-block-start: 0;
      }

      &:has(+ [role='tabpanel']) {
        padding-block-end: 0;
      }

      > ${StyledTabs}, > ${StyledAllTabsWrapper} {
        min-inline-size: calc(100% + ${theme.components.card.padding} * 4);
        margin-inline-start: calc(${theme.components.card.padding} * -2);
      }

      + ${StyledCardContent} {
        padding-block-start: 0;

        ${StyledProgressBackdrop} {
          border-end-start-radius: ${theme.components.card['border-radius']};
          border-end-end-radius: ${theme.components.card['border-radius']};
        }

        /* Plain selector is used instead of StyledTabPanel due to cyclic dependency. */

        > [role='tabpanel'] {
          padding-block-start: 0;
        }
      }

      &:hover {
        ${onClick ? 'cursor: pointer;' : undefined}
      }
    }
  `;
});

StyledCardHeader.defaultProps = defaultThemeProp;

const CardHeader: ForwardRefForwardPropsComponent<CardHeaderProps> = forwardRef(function CardHeader(
  { children, actions, ...restProps }: PropsWithoutRef<CardHeaderProps>,
  ref: CardHeaderProps['ref']
) {
  return (
    <Flex
      container={{ alignItems: 'center', justify: 'between' }}
      as={StyledCardHeader}
      {...restProps}
      ref={ref}
    >
      {actions ? (
        <>
          <Flex container={{ alignItems: 'center' }} item={{ grow: 1 }}>
            {children}
          </Flex>
          <div>{actions}</div>
        </>
      ) : (
        children
      )}
    </Flex>
  );
});

CardHeader.displayName = 'CardHeader';
export default CardHeader;
